import { httpClient, unwrap, unwrapPaginated } from '@/services/httpClient';
import type {
  AiCommand,
  AiConversation,
  AiSendMessageResult,
  CalendarEvent,
  ChatMessage,
  ChatRoom,
  Workspace,
  WorkspaceAnalytics,
  WorkspaceDashboardSummary,
  WorkspaceMember,
  WorkspaceMemberRole,
  WorkspaceSettings,
  WorkspaceVisibility,
} from '@/features/workspaces/types';

export interface WorkspacePayload {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  visibility?: WorkspaceVisibility;
}

export interface WorkspaceFilters {
  visibility?: WorkspaceVisibility | '';
  ownerId?: number;
}

export interface EventPayload {
  title: string;
  description?: string;
  workspace_id?: number | null;
  project_id?: number | null;
  event_type?: string;
  all_day?: boolean;
  start_at: string;
  end_at: string;
  location?: string;
  color?: string;
}

export interface CreateRoomPayload {
  name?: string;
  workspace_id?: number;
  project_id?: number;
  room_type?: 'group' | 'direct';
  is_private?: boolean;
}

export const workspacesApi = {
  list: (page: number, perPage: number, search: string, sort: string, order: 'asc' | 'desc', filters?: WorkspaceFilters) =>
    unwrapPaginated<Workspace>(
      httpClient.get('/workspaces', {
        params: { page, per_page: perPage, search: search || undefined, sort, order, visibility: filters?.visibility || undefined, owner_id: filters?.ownerId || undefined },
      }),
    ),

  get: (id: number) => unwrap<Workspace>(httpClient.get(`/workspaces/${id}`)),

  create: (payload: WorkspacePayload) => unwrap<Workspace>(httpClient.post('/workspaces', payload)),

  update: (id: number, payload: Partial<WorkspacePayload>) => unwrap<Workspace>(httpClient.put(`/workspaces/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/workspaces/${id}`)),

  uploadLogo: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return unwrap<{ logo_url: string }>(
      httpClient.post(`/workspaces/${id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
  },

  addMember: (id: number, userId: number, role: WorkspaceMemberRole = 'member') =>
    unwrap<WorkspaceMember[]>(httpClient.post(`/workspaces/${id}/members`, { user_id: userId, role_in_workspace: role })),

  updateMemberRole: (id: number, userId: number, role: WorkspaceMemberRole) =>
    unwrap<WorkspaceMember[]>(httpClient.put(`/workspaces/${id}/members/${userId}`, { role_in_workspace: role })),

  removeMember: (id: number, userId: number) => unwrap<WorkspaceMember[]>(httpClient.delete(`/workspaces/${id}/members/${userId}`)),

  getSettings: (id: number) => unwrap<WorkspaceSettings>(httpClient.get(`/workspaces/${id}/settings`)),

  updateSettings: (id: number, payload: Partial<WorkspaceSettings>) =>
    unwrap<WorkspaceSettings>(httpClient.put(`/workspaces/${id}/settings`, payload)),

  dashboard: (id: number) => unwrap<WorkspaceDashboardSummary>(httpClient.get(`/workspaces/${id}/dashboard`)),

  analytics: (id: number) => unwrap<WorkspaceAnalytics>(httpClient.get(`/workspaces/${id}/analytics`)),
};

export const calendarApi = {
  list: (filters: { workspaceId?: number; projectId?: number; from?: string; to?: string }) =>
    unwrapPaginated<CalendarEvent>(
      httpClient.get('/calendar/events', {
        params: { workspace_id: filters.workspaceId, project_id: filters.projectId, per_page: 100, from: filters.from, to: filters.to },
      }),
    ).then((r) => r.data),

  get: (id: number) => unwrap<CalendarEvent>(httpClient.get(`/calendar/events/${id}`)),

  create: (payload: EventPayload) => unwrap<CalendarEvent>(httpClient.post('/calendar/events', payload)),

  update: (id: number, payload: Partial<EventPayload>) => unwrap<CalendarEvent>(httpClient.put(`/calendar/events/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/calendar/events/${id}`)),
};

export const chatApi = {
  rooms: (workspaceId: number) =>
    unwrapPaginated<ChatRoom>(httpClient.get('/chat/rooms', { params: { workspace_id: workspaceId, per_page: 50 } })).then((r) => r.data),

  getRoom: (id: number) => unwrap<ChatRoom>(httpClient.get(`/chat/rooms/${id}`)),

  createRoom: (payload: CreateRoomPayload) => unwrap<ChatRoom>(httpClient.post('/chat/rooms', payload)),

  updateRoom: (id: number, payload: { name?: string; is_private?: boolean }) => unwrap<ChatRoom>(httpClient.put(`/chat/rooms/${id}`, payload)),

  removeRoom: (id: number) => unwrap<null>(httpClient.delete(`/chat/rooms/${id}`)),

  messages: (roomId: number, page = 1) =>
    unwrapPaginated<ChatMessage>(httpClient.get(`/chat/rooms/${roomId}/messages`, { params: { page, per_page: 50 } })),

  messagesAfter: (roomId: number, afterId: number) =>
    unwrap<ChatMessage[]>(httpClient.get(`/chat/rooms/${roomId}/messages`, { params: { after_id: afterId } })),

  sendMessage: (roomId: number, content: string, parentMessageId?: number) =>
    unwrap<ChatMessage>(httpClient.post(`/chat/rooms/${roomId}/messages`, { content, parent_message_id: parentMessageId })),

  updateMessage: (messageId: number, content: string) => unwrap<ChatMessage>(httpClient.put(`/chat/messages/${messageId}`, { content })),

  removeMessage: (messageId: number) => unwrap<null>(httpClient.delete(`/chat/messages/${messageId}`)),

  markRead: (roomId: number) => unwrap<{ message: string }>(httpClient.post(`/chat/rooms/${roomId}/read`)),

  addMember: (roomId: number, userId: number) => unwrap<unknown>(httpClient.post(`/chat/rooms/${roomId}/members`, { user_id: userId })),

  removeMember: (roomId: number, userId: number) => unwrap<unknown>(httpClient.delete(`/chat/rooms/${roomId}/members/${userId}`)),
};

export const aiApi = {
  conversations: (contextType: string, contextId: number) =>
    unwrap<AiConversation[]>(httpClient.get('/ai/conversations', { params: { context_type: contextType, context_id: contextId } })),

  createConversation: (contextType: string, contextId: number, title?: string) =>
    unwrap<AiConversation>(httpClient.post('/ai/conversations', { context_type: contextType, context_id: contextId, title })),

  getConversation: (id: number) => unwrap<AiConversation>(httpClient.get(`/ai/conversations/${id}`)),

  removeConversation: (id: number) => unwrap<null>(httpClient.delete(`/ai/conversations/${id}`)),

  sendMessage: (conversationId: number, content: string) =>
    unwrap<AiSendMessageResult>(httpClient.post(`/ai/conversations/${conversationId}/messages`, { content })),

  commands: () => unwrap<AiCommand[]>(httpClient.get('/ai/commands')),
};
